import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import withStyles from '@material-ui/core/styles/withStyles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth/withWidth';
import SelectionToolbox from 'redhotmagma-graphics-editor/Components/SelectionToolbox/SelectionToolbox';
import ReactComponent from '@configuratorware/configurator-frontendgui/Shared/PropTypes/ReactComponent';
import { isPlaceholderContent } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Selectors';
import isMobileDevice from '@configuratorware/configurator-frontendgui/Framework/Helpers/isMobileDevice';
import { hasBackgroundColor } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignData/Selectors';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import ImageControlbox from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Components/TextControlbox';
import { CustomFontOverlay } from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Components/DesignerSelectionToolbox/CustomFontOverlay';
import { withCanvasState } from 'redhotmagma-graphics-editor/CanvasInterface/CanvasProvider';
import { createFontItemProps, findCurrentFontItem } from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Components/DesignerSelectionToolbox/fontItemUtils';
import TextControlbox from '../TextControlbox';
import MobileMenuStep from '../../../../Shared/Components/MobileMenuStep';

const styles = (theme) => ({
  root: {
    // marginTop: theme.spacing(2) + theme.layout.productsList.height,
    // marginBottom: theme.spacing(2),
    position: 'absolute',
    bottom: 0,
    width: 300,
    left: 0,
    pointerEvents: 'all',
    zIndex: 9,

  },
  disableTextEditor: {
    userSelect: 'none',
  },
  detailList: {
    padding: 0,
    margin: theme.spacing(3),
  },
});

const isButtonDisabled = (isBold, isItalic, fontItem, action) => getFontNextState(
  isBold,
  isItalic,
  fontItem.regular,
  fontItem.bold,
  fontItem.italic,
  fontItem.boldItalic,
  action,
) === null;

const getFontNextState = (
  currentBold,
  currentItalic,
  regularAvailable,
  boldAvailable,
  italicAvailable,
  boldItalicAvailable,
  action,
) => {
  const acceptableOutcomes = [];

  if (action === 'bold') {
    acceptableOutcomes.push({ bold: !currentBold, italic: currentItalic });
  } else if (action === 'italic') {
    acceptableOutcomes.push({ bold: currentBold, italic: !currentItalic });
  }

  acceptableOutcomes.push({ bold: !currentBold, italic: !currentItalic });

  const availableAcceptableOutcomes = acceptableOutcomes.filter((acceptableOutcome) => {
    if (acceptableOutcome.bold) {
      return acceptableOutcome.italic ? boldItalicAvailable : boldAvailable;
    }

    return acceptableOutcome.italic ? italicAvailable : regularAvailable;
  });

  return availableAcceptableOutcomes.length ? availableAcceptableOutcomes[0] : null;
};

const handleObjectButtonClick = (evt, canvas, onMoveUpClick, onMoveDownClick) => {
  switch (evt.currentTarget.value) {
    case 'remove': {
      canvas.selectedObject.remove();
      break;
    }
    case 'moveUp': {
      onMoveUpClick();
      break;
    }
    case 'moveDown': {
      onMoveDownClick();
      break;
    }
    case 'alignHorizontalCenter': {
      canvas.selectedObject.alignTo(false, 'center');
      break;
    }
    case 'alignVerticalCenter': {
      canvas.selectedObject.alignTo('center', false);
      break;
    }
  }
};

export const isOriginallyVector = (object) => (object
        && object.type === 'Image'
        && /^(eps|pdf|ai|svg)$/i.test(get(object, 'imageData.original.format', '')))
    || false;

const isRaster = (object) => (object && object.type === 'Image' && /\.(jpe?g|png|gif)$/i.test(object.src)) || false;

export const getPrintQuality = (object) => {
  if (!isOriginallyVector(object) && isRaster(object)) {
    if (object.scaling >= 2) {
      return 'poor';
    }
    if (object.scaling > 1) {
      return 'ok';
    }
  }
  return 'great';
};

const DesignerSelectionToolbox = (props) => {
  const {
    classes,
    width,
    TextControlboxComponent,
    CustomFontOverlayComponent,
    colorDisabled,
    fonts: fontFamilyList,
    isActiveBulkName,
    getTextContent,
    isPortraitMode,
    addCustomFont,
    defaultFont,
    loadFonts, selectedObject, replaceTextEditDialogCanvas,
    initialColor, designDataForSelectedDesignArea,
    setModalContentOption, modalContentOptions, currentModalContentOption, closeDesignArea,
  } = props;

  const rteRef = useRef(null);
  const editorRef = useRef(null);
  const initialDesignArea = useRef(null);
  const [showCustomFontOverlay, toggleCustomFontOverlay] = useState(false);

  useEffect(() => {
    if (!selectedObject) return;

    if (selectedObject.type === 'Text') {
      if (initialDesignArea.current !== null) return;
      initialDesignArea.current = designDataForSelectedDesignArea;
    } else {
      initialDesignArea.current = null;
    }
  }, [selectedObject, initialDesignArea, designDataForSelectedDesignArea]);

  return (
    <div className={classes.root}>
      <SelectionToolbox>
        {({
          canvas, selectedObject, onMoveUpClick, onMoveDownClick, components: { TextTools },
          closeMobilePickerMenu,
        }) => {
          const textTools = (
            <TextTools
              selectedObject={selectedObject}
              canvas={canvas}
              fullTextCommandList={['fontSize', 'fontFamily']}
              withStateProvider
              fontFamilyList={fontFamilyList}
              rteRef={rteRef}
              editorRef={editorRef}
            >
              {(textToolsRenderObj) => {
                const {
                  canvas,
                  selectedObject,
                  classes: textToolsClasses,
                  renderTextEditor,
                  onChangeHTML,
                  textEditorState: { formatDoc, getStyleValues },
                  fontFamilyList,
                } = textToolsRenderObj;
                const {
                  isBold, isItalic, textAlign, fontSize,
                } = getStyleValues();
                const color = isEmpty(initialColor)
                  ? cloneDeep(getStyleValues().color)
                  : initialColor;
                const editorBackgroundColor = hasBackgroundColor(
                  selectedObject.style.colors.length > 0
                    ? selectedObject.style.colors
                    : [color],
                )
                  ? '#C4C4C4'
                  : '#FFFFFF';

                const currentFontItem = findCurrentFontItem(
                  fontFamilyList,
                  selectedObject.fontItem,
                );

                // add custom font item
                const translatedAddCustomFont = t('textControlbox.addCustomFont');
                const addCustomFontItem = {
                  name: translatedAddCustomFont,
                  value: translatedAddCustomFont,
                };
                const fontsList = [...fontFamilyList, addCustomFontItem];

                return (
                  <>
                    <TextControlboxComponent
                      selectedObject={selectedObject}
                      isActiveBulkName={isActiveBulkName}
                      boxed={width !== 'xs'}
                      isPortraitMode={isPortraitMode}
                      textValue={selectedObject.content}
                      handleChangeText={onChangeHTML}
                      fontValue={currentFontItem}
                      fonts={fontsList}
                      editorBackgroundColor={editorBackgroundColor}
                      color={color}
                      handleChangeFont={(evt) => {
                        const fontItem = evt.target.value;

                        if (fontItem === addCustomFontItem) {
                          toggleCustomFontOverlay(true);
                          return;
                        }

                        canvas.selectedObject.fontItem = createFontItemProps(
                          fontItem,
                        );
                      }}
                      minSize={selectedObject.fontSizeMin}
                      maxSize={1000}
                      fontSizeValue={parseInt(fontSize)}
                      onChangeFontSize={(value) => formatDoc('fontSize', `${value}px`)}
                      handleButtonClick={(evt) => {
                        const command = evt.currentTarget.value;

                        if (command === 'bold' || command === 'italic') {
                          const font = currentFontItem;

                          const fontNextState = getFontNextState(
                            isBold,
                            isItalic,
                            font.regular,
                            font.bold,
                            font.italic,
                            font.boldItalic,
                            command,
                          );

                          if (fontNextState === null) {
                            return;
                          }

                          if (fontNextState.bold !== isBold) {
                            formatDoc('bold');
                          }

                          if (fontNextState.italic !== isItalic) {
                            formatDoc('italic');
                          }

                          return;
                        }

                        formatDoc(command);
                      }}
                      handleObjectButtonClick={(evt) => {
                        handleObjectButtonClick(
                          evt,
                          canvas,
                          onMoveUpClick,
                          onMoveDownClick,
                        );
                      }}
                      flipToBackDisabled={selectedObject.isFirst}
                      flipToFrontDisabled={selectedObject.isLast}
                      renderEditorComponent={() => {
                        const textEditorElementProps = {
                          onFocus: () => {
                            if (
                              isPlaceholderContent(selectedObject.content)
                            ) {
                              canvas.selectedObject.content = '';
                            }
                          },
                        };

                        if (selectedObject.isBulkName) {
                          Object.assign(textEditorElementProps, {
                            contentEditable: false,
                            className: [
                              textToolsClasses.textEditorContent,
                              classes.disableTextEditor,
                            ].join(' '),
                          });
                        }

                        return renderTextEditor({
                          ...textToolsRenderObj,
                          autoFocusWhenMounted: !(
                            isPlaceholderContent(selectedObject.content)
                                                          || isMobileDevice()
                          ),
                          textEditorElementProps,
                          onChangeHTML: (...args) => {
                            // call the original function first with all the args
                            textToolsRenderObj.onChangeHTML(...args);
                            // reload the fonts
                            loadFonts(canvas.selectedObject);
                          },
                        });
                      }}
                      isBold={isBold}
                      boldDisabled={isButtonDisabled(
                        isBold,
                        isItalic,
                        currentFontItem,
                        'bold',
                      )}
                      isItalic={isItalic}
                      italicDisabled={isButtonDisabled(
                        isBold,
                        isItalic,
                        currentFontItem,
                        'italic',
                      )}
                      textAlign={textAlign}
                      colorDisabled={colorDisabled}
                      isBulkName={selectedObject.isBulkName}
                      toggleIsBulkName={() => {
                        const isBulkName = !selectedObject.isBulkName;
                        canvas.selectedObject.isBulkName = isBulkName;
                        canvas.selectedObject.content = getTextContent({
                          isBulkName,
                        });
                      }}
                    />
                    <CustomFontOverlayComponent
                      onClose={() => { toggleCustomFontOverlay(false); }}
                      open={showCustomFontOverlay}
                      onApply={(fontName) => {
                        const fontItem = {
                          ...defaultFont,
                          customName: fontName,
                          isCustomFont: true,
                        };
                        addCustomFont(fontItem);
                        canvas.selectedObject.fontItem = createFontItemProps(
                          fontItem,
                        );
                        toggleCustomFontOverlay(false);
                      }}
                    />
                  </>
                );
              }}
            </TextTools>
          );
          if (selectedObject.type === 'Text') {
            if (!isWidthUp('sm', width)) {
              return (
                <MobileMenuStep
                  doDisplay={selectedObject.type === 'Text' && currentModalContentOption === modalContentOptions.RENDER_EDITTEXT}
                  renderStep={() => (
                    <div className={classes.detailList}>
                      {textTools}
                    </div>
                  )}
                  doDisplayDesignArea
                  heading={t('fontToolbox.addText')}
                  backFunc={() => {
                    replaceTextEditDialogCanvas(initialDesignArea.current);
                    if (currentModalContentOption) {
                      setModalContentOption(modalContentOptions.RENDER_ITEMMENU);
                    }
                  }}
                  closeFunc={null}
                  renderActionButtonProps={{
                    title: t('fontToolbox.editText'),
                    onClick: () => {
                      initialDesignArea.current = designDataForSelectedDesignArea;
                      if (currentModalContentOption) {
                        closeDesignArea();
                        setModalContentOption(null);
                        closeMobilePickerMenu();
                      }
                    },
                  }}
                />
              );
            }
            return textTools;
          }
          // do not show control box for unknown object types
          return null;
        }}
      </SelectionToolbox>
    </div>
  );
};

DesignerSelectionToolbox.propTypes = {
  updateVisualization: PropTypes.func.isRequired,
  openImageEditDialog: PropTypes.func.isRequired,
  loadFonts: PropTypes.func.isRequired,
  TextControlboxComponent: ReactComponent,
  ImageControlboxComponent: ReactComponent,
  CustomFontOverlayComponent: PropTypes.elementType,
  fonts: PropTypes.array,
  colorDisabled: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string,
    disableTextEditor: PropTypes.string,
  }),
  width: PropTypes.string,
  isActiveBulkName: PropTypes.bool,
  getTextContent: PropTypes.func,
  isPortraitMode: PropTypes.bool,
  calculateTextEditorBackgroundColor: PropTypes.func,
  addCustomFont: PropTypes.func,
  defaultFont: PropTypes.object,
  initialColor: PropTypes.object,
};

DesignerSelectionToolbox.defaultProps = {
  TextControlboxComponent: TextControlbox,
  ImageControlboxComponent: ImageControlbox,
  CustomFontOverlayComponent: CustomFontOverlay,
  fonts: [],
  colorDisabled: false,
  classes: {},
  width: '',
  isActiveBulkName: false,
  isPortraitMode: false,
};

export default withWidth()(
  withStyles(styles, { name: 'DesignerSelectionToolbox' })(withCanvasState(DesignerSelectionToolbox)),
);
